import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropertySVGOverlay from './PropertySVGOverlay';
import PropertySVGOverlayV2 from './PropertySVGOverlayV2';
import { getResponsiveViewBox, convertLegacyCoordinates, USE_NAVIGATOR_V2 } from '../config/mapConfig';
import { isNavigatorV3, NavigatorV3 } from './navigator/Navigator';
import './InteractivePropertyMap.css';

function InteractivePropertyMap(props) {
  // ============================================================
  // VERSION DISPATCH — V3 takes over rendering for opted-in tenants
  // (navigator_config_v2.version === 'v3'). V2 path below stays as
  // it was on main, untouched, for existing tenants.
  // ============================================================
  if (isNavigatorV3(props.investmentData)) {
    return <NavigatorV3 {...props} />;
  }

  return <InteractivePropertyMapV2 {...props} />;
}

function InteractivePropertyMapV2({
  properties = [],
  filteredProperties = [],
  investmentData,
  selectedPropertyId,
  onPropertySelect
}) {
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [currentArtboard, setCurrentArtboard] = useState(null);
  const containerRef = useRef(null);

  const handlePropertyClick = useCallback((propertyId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (onPropertySelect) {
      onPropertySelect(propertyId);
    }
  }, [onPropertySelect]);

  const handlePropertyHover = useCallback((propertyId) => {
    setHoveredProperty(propertyId);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    console.error('Investment map image failed to load');
  }, []);

  const handleImageRetry = useCallback(() => {
    setImageError(false);
  }, []);

  const handleArtboardChange = useCallback((artboard) => {
    setCurrentArtboard(artboard);
    // Reset image error when artboard changes
    setImageError(false);
  }, []);

  // Handle responsive viewBox updates
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    // Set initial width
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter properties that have map coordinates and convert legacy coordinates
  const propertiesWithCoordinates = properties
    .filter(property => property.mapCoordinates && property.mapCoordinates.svgPath)
    .map(property => ({
      ...property,
      mapCoordinates: convertLegacyCoordinates(property.mapCoordinates)
    }));

  // Fallback UI when image fails to load
  if (imageError) {
    return (
      <div className="interactive-property-map">
        <div className="map-error">
          <div className="map-error-content">
            <p>Mapa inwestycji jest tymczasowo niedostępna</p>
            <button 
              className="retry-button" 
              onClick={handleImageRetry}
              type="button"
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if we should use V2 format
  const useV2Format = USE_NAVIGATOR_V2 && investmentData?.navigatorConfigV2;

  // Don't render if no investment data or no map configuration
  if (!investmentData || !investmentData.mapConfiguration?.imageUrl) {
    return null;
  }

  // Use current artboard image if available (for V2 navigation)
  const mapImageUrl = currentArtboard?.image?.url ||
                     investmentData.mapConfiguration?.imageUrl ||
                     'https://kmadenski.github.io/deldom-navigator-mvp/assets/wiz.jpg';

  const mapImageAlt = currentArtboard?.name ||
                     investmentData.mapConfiguration?.imageAlt ||
                     'Plan inwestycji';

  // Get viewBox and aspectRatio - prefer current artboard settings for V2
  let viewBox = currentArtboard?.viewBox ||
               investmentData.mapConfiguration?.viewBox ||
               getResponsiveViewBox(containerWidth);

  // Calculate aspectRatio from current artboard image dimensions or use defaults
  let aspectRatio = currentArtboard?.image?.width && currentArtboard?.image?.height
    ? currentArtboard.image.width / currentArtboard.image.height
    : investmentData.mapConfiguration?.aspectRatio || 1.77;

  // V1 fallback: Override viewBox for antoninow-park to match actual image coordinates
  if (!useV2Format && investmentData.slug === 'antoninow-park') {
    viewBox = '0 0 1549 769';
    aspectRatio = 1549 / 769;
  }


  return (
    <div className="interactive-property-map" ref={containerRef}>
      <div className="map-container">
        <img
          src={mapImageUrl}
          alt={mapImageAlt}
          className="investment-image"
          style={{ aspectRatio: aspectRatio }}
          onError={handleImageError}
          loading="lazy"
        />

        {useV2Format ? (
          <PropertySVGOverlayV2
            navigatorConfig={investmentData.navigatorConfigV2}
            properties={properties}
            selectedProperty={selectedPropertyId}
            hoveredProperty={hoveredProperty}
            onPropertyClick={handlePropertyClick}
            onPropertyHover={handlePropertyHover}
            onArtboardChange={handleArtboardChange}
            viewBox={viewBox}
            enableNavigatorTooltips={investmentData.enableNavigatorTooltips}
          />
        ) : (
          <PropertySVGOverlay
            properties={propertiesWithCoordinates}
            selectedProperty={selectedPropertyId}
            hoveredProperty={hoveredProperty}
            onPropertyClick={handlePropertyClick}
            onPropertyHover={handlePropertyHover}
            viewBox={viewBox}
          />
        )}
      </div>

      {!useV2Format && propertiesWithCoordinates.length < properties.length && (
        <div className="map-notice">
          <small>
            Wyświetlane są tylko nieruchomości z dostępnymi współrzędnymi na mapie
            ({propertiesWithCoordinates.length} z {properties.length})
          </small>
        </div>
      )}
    </div>
  );
}

export default InteractivePropertyMap;